import React, { useState, useEffect, useCallback } from "react";
import { Card, Select } from "antd";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { toast } from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../interceptors/api";
import StandardButton from "../components/StandardButton";

const { Option } = Select;

const RemoveLineBreaks = () => {
  const defaultText = "This is a sample text.\nWith line breaks,\r\nand various\rformats.";
  const [input, setInput] = useState(defaultText);
  const [replacement, setReplacement] = useState(" ");
  const [collapseSpaces, setCollapseSpaces] = useState(false);
  const [output, setOutput] = useState("");
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  useEffect(() => {
    const id = searchParams.get("share");
    if (id) {
      api.get(`/remove-line-breaks/share/${id}`)
        .then(res => {
          const data = res.data;
          if (data.input && data.result) {
            setInput(data.input);
            setOutput(data.result);
            setReplacement(data.replacement || " ");
          }
        })
        .catch(() => {
          toast.error(t('server_error'));
        });
    }
  }, []);

  const handleRemoveLineBreaks = useCallback(async () => {
    try {
      const res = await api.post(`/remove-line-breaks`, { text: input, replacement, collapseSpaces });
      const data = res.data;
      if (data.result) {
        setOutput(data.result);
      } else {
        setOutput(t('error') + ": " + data.error);
      }
    } catch {
      setOutput(t('server_error'));
    }
  }, [input, replacement, collapseSpaces, t]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "Enter" || !(e.metaKey || e.ctrlKey)) return;
      if (e.altKey || e.shiftKey) return;
      e.preventDefault();
      handleRemoveLineBreaks();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleRemoveLineBreaks]);

  const handleReplacementChange = (value) => {
    setReplacement(value === "\\n" ? "\n" : value);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(output);
      toast.success(t('copy_success'));
    } catch {
      toast.error(t('failed_to_copy'));
    }
  };

  const handleShare = async () => {
    try {
      const res = await api.post(`/remove-line-breaks/share`, {
        input,
        result: output,
        replacement: replacement === "\n" ? "\\n" : replacement,
      });

      const data = res.data;
      if (data.id) {
        const url = `${window.location.origin}/tools/remove-line-breaks?share=${data.id}`;
        await navigator.clipboard.writeText(url);
        toast.success(t('share_success'));
      } else {
        toast.error(t('share_failed'));
      }
    } catch {
      toast.error(t('share_failed'));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <div>
              <h1 className="text-2xl font-bold text-indigo-700">
                {t('remove_line_breaks_title')}
              </h1>
              <p className="text-gray-600 mt-1">{t('remove_line_breaks_intro')}</p>
            </div>

            <textarea
              rows={6}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
              placeholder={t('remove_line_breaks_placeholder')}
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />

            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                {t('replace_line_breaks_with')}
              </label>
              <Select
                className="w-full"
                value={replacement === "\n" ? "\\n" : replacement}
                onChange={handleReplacementChange}
              >
                <Option value=" ">{t('space')}</Option>
                <Option value=",">{t('comma')}</Option>
                <Option value="|">{t('pipe')}</Option>
                <Option value="-">{t('dash')}</Option>
                <Option value="\\n">{t('new_line')}</Option>
                <Option value="">{t('nothing')}</Option>
              </Select>
            </div>

            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={collapseSpaces}
                onChange={(e) => setCollapseSpaces(e.target.checked)}
                className="h-4 w-4 mt-0.5"
              />
              <span>
                <span className="font-medium">{t('remove_line_breaks_collapse_spaces')}</span>
                <span className="block text-xs text-gray-500">
                  {t('remove_line_breaks_collapse_spaces_help')}
                </span>
              </span>
            </label>

            <StandardButton
              onClick={handleRemoveLineBreaks}
              type="secondary"
              className="w-full"
            >
              {t('remove_line_breaks_button')}
            </StandardButton>

            {output && (
              <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200 whitespace-pre-wrap">
                <strong className="text-indigo-700">{t('output')}:</strong>
                <p className="break-all mt-2 text-gray-800">{output}</p>
                <div className="mt-2 flex gap-3">
                  <StandardButton
                    onClick={handleCopy}
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t('copy_button')}
                  </StandardButton>
                  <StandardButton
                    onClick={handleShare}
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t('share_button')}
                  </StandardButton>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default RemoveLineBreaks;
